import type { Directive, DirectiveBinding } from 'vue';

type ImageLoadingValue =
  | string
  | {
      label?: string;
      subLabel?: string;
      errorLabel?: string;
      errorSubLabel?: string;
    };

interface ImageLoadingState {
  overlay: HTMLSpanElement;
  parent: HTMLElement;
  onLoad: () => void;
  onError: () => void;
  resizeObserver?: ResizeObserver;
}

type ImageLoadingElement = HTMLImageElement & {
  __studioImageLoading?: ImageLoadingState;
};

const DEFAULT_LABEL = '图片加载中';
const DEFAULT_SUB_LABEL = '正在加载高清图';
const DEFAULT_ERROR_LABEL = '图片加载失败';
const DEFAULT_ERROR_SUB_LABEL = '请刷新页面或稍后重试';

function normalizeValue(binding: DirectiveBinding<ImageLoadingValue>) {
  const value = binding.value;
  if (typeof value === 'string') {
    return {
      label: value || DEFAULT_LABEL,
      subLabel: DEFAULT_SUB_LABEL,
      errorLabel: DEFAULT_ERROR_LABEL,
      errorSubLabel: DEFAULT_ERROR_SUB_LABEL
    };
  }
  return {
    label: value?.label || DEFAULT_LABEL,
    subLabel: value?.subLabel || DEFAULT_SUB_LABEL,
    errorLabel: value?.errorLabel || DEFAULT_ERROR_LABEL,
    errorSubLabel: value?.errorSubLabel || DEFAULT_ERROR_SUB_LABEL
  };
}

function updateOverlayCopy(el: ImageLoadingElement, binding: DirectiveBinding<ImageLoadingValue>) {
  const state = el.__studioImageLoading;
  if (!state) return;
  const copy = normalizeValue(binding);
  state.overlay.querySelector('b')!.textContent = copy.label;
  state.overlay.querySelector('em')!.textContent = copy.subLabel;
  state.overlay.dataset.errorLabel = copy.errorLabel;
  state.overlay.dataset.errorSubLabel = copy.errorSubLabel;
}

function setCompactBySize(parent: HTMLElement, forceCompact: boolean) {
  const hasMeasuredSize = parent.clientWidth > 0 && parent.clientHeight > 0;
  const compact = forceCompact || (hasMeasuredSize && (parent.clientWidth < 116 || parent.clientHeight < 92));
  parent.classList.toggle('is-studio-image-loading-compact', compact);
}

function ensureState(el: ImageLoadingElement, binding: DirectiveBinding<ImageLoadingValue>) {
  if (el.__studioImageLoading) return el.__studioImageLoading;
  const parent = el.parentElement;
  if (!parent) return null;

  const overlay = document.createElement('span');
  overlay.className = 'studio-image-loading-overlay';
  overlay.innerHTML = '<span class="studio-image-loading-spinner"></span><b></b><em></em>';

  parent.classList.add('studio-image-loading-host');
  if (window.getComputedStyle(parent).position === 'static') {
    parent.classList.add('studio-image-loading-host-static');
  }
  el.classList.add('studio-image-loading-img');
  parent.appendChild(overlay);

  const state: ImageLoadingState = {
    overlay,
    parent,
    onLoad: () => setImageState(el, 'loaded'),
    onError: () => setImageState(el, 'error')
  };

  if (typeof ResizeObserver !== 'undefined') {
    state.resizeObserver = new ResizeObserver(() => setCompactBySize(parent, !!binding.modifiers.compact));
    state.resizeObserver.observe(parent);
  }

  el.addEventListener('load', state.onLoad);
  el.addEventListener('error', state.onError);
  el.__studioImageLoading = state;
  updateOverlayCopy(el, binding);
  setCompactBySize(parent, !!binding.modifiers.compact);
  return state;
}

function setImageState(el: ImageLoadingElement, state: 'loading' | 'loaded' | 'error' | 'empty') {
  const data = el.__studioImageLoading;
  if (!data) return;
  data.overlay.dataset.state = state;
  data.parent.classList.toggle('is-studio-image-loading-active', state === 'loading' || state === 'error');
  data.parent.classList.toggle('is-studio-image-loading-error', state === 'error');
  el.classList.toggle('is-studio-image-loaded', state === 'loaded');
  el.classList.toggle('is-studio-image-error', state === 'error');

  if (state === 'error') {
    const label = data.overlay.dataset.errorLabel || DEFAULT_ERROR_LABEL;
    const subLabel = data.overlay.dataset.errorSubLabel || DEFAULT_ERROR_SUB_LABEL;
    data.overlay.querySelector('b')!.textContent = label;
    data.overlay.querySelector('em')!.textContent = subLabel;
  }
}

function syncImageState(el: ImageLoadingElement, binding: DirectiveBinding<ImageLoadingValue>) {
  const state = ensureState(el, binding);
  if (!state) return;
  updateOverlayCopy(el, binding);

  const src = el.currentSrc || el.src || el.getAttribute('src');
  if (!src) {
    setImageState(el, 'empty');
    return;
  }

  if (el.complete) {
    setImageState(el, el.naturalWidth > 0 ? 'loaded' : 'error');
  } else {
    setImageState(el, 'loading');
  }
}

const imageLoading: Directive<HTMLImageElement, ImageLoadingValue> = {
  mounted(el, binding) {
    requestAnimationFrame(() => syncImageState(el as ImageLoadingElement, binding));
  },
  updated(el, binding) {
    requestAnimationFrame(() => syncImageState(el as ImageLoadingElement, binding));
  },
  beforeUnmount(el) {
    const target = el as ImageLoadingElement;
    const state = target.__studioImageLoading;
    if (!state) return;
    target.removeEventListener('load', state.onLoad);
    target.removeEventListener('error', state.onError);
    state.resizeObserver?.disconnect();
    state.overlay.remove();
    state.parent.classList.remove(
      'studio-image-loading-host',
      'studio-image-loading-host-static',
      'is-studio-image-loading-compact',
      'is-studio-image-loading-active',
      'is-studio-image-loading-error'
    );
    target.classList.remove('studio-image-loading-img', 'is-studio-image-loaded', 'is-studio-image-error');
    delete target.__studioImageLoading;
  }
};

export default imageLoading;
